import React from 'react'
import { WordLength } from '../types/game'
import { FaLock } from 'react-icons/fa'
import { CompletedGameDetail } from '../utils/storage'

interface ChallengeCardProps {
  wordLength: WordLength
  description: string
  examples: string[]
  isLocked: boolean
  hasPlayedToday: boolean
  playedGameDetail?: CompletedGameDetail
  revealedWord?: string
  revealedWon?: boolean
  daysCompleted?: number
  totalDays?: number
  onPlay: () => void
}

export const ChallengeCard: React.FC<ChallengeCardProps> = ({
  wordLength,
  description,
  examples,
  isLocked,
  hasPlayedToday,
  playedGameDetail,
  revealedWord,
  revealedWon,
  daysCompleted = 0,
  totalDays = 3,
  onPlay,
}) => {
  const progressPercent = Math.round((daysCompleted / totalDays) * 100)

  // Determine the button state and text
  const isDisabled = isLocked || hasPlayedToday
  const buttonText = hasPlayedToday
    ? 'Already Played'
    : isLocked
      ? '' // No text for locked buttons
      : 'Play Now'

  // Determine aria-label for accessibility
  const ariaLabel = `${hasPlayedToday ? 'Already played today' : isLocked ? 'Locked' : ''} ${wordLength}-letter challenge`

  return (
    <div
      className={`mode-card ${hasPlayedToday ? 'opacity-60' : ''} ${isLocked && !hasPlayedToday ? 'opacity-40 bg-gray-800/50' : ''}`}
      style={{
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      data-hj-clickable="true"
    >
      <h2>{wordLength} Letters</h2>
      <p>
        {hasPlayedToday
          ? description
          : isLocked
            ? 'Locked - Complete easier levels first'
            : description}
      </p>

      {/* Display last guess state if played today, otherwise show static examples */}
      <div className="letter-examples">
        {hasPlayedToday && playedGameDetail?.guesses ? (
          (() => {
            // Find the last non-empty guess row submitted by the user
            const lastGuessRow = [...playedGameDetail.guesses]
              .reverse()
              .find((row) => row.some((tile) => tile.letter !== ''))

            if (lastGuessRow) {
              return lastGuessRow.map((tile, index) => {
                const state = tile.state
                const stateClass =
                  state === 'correct'
                    ? 'bg-green-500 text-white border-green-500'
                    : state === 'wrong-location'
                    ? 'bg-yellow-500 text-white border-yellow-500'
                    : ''
                return (
                  <div
                    key={index}
                    className={`letter-box ${stateClass} uppercase`}
                  >
                    {tile.letter.toUpperCase()}
                  </div>
                )
              })
            }

            // Fallback if no guesses found
            return examples.map((letter, index) => (
              <div
                key={index}
                className={`letter-box ${isLocked && !hasPlayedToday ? 'bg-gray-700' : ''}`}
              >
                {isLocked && !hasPlayedToday ? '?' : letter}
              </div>
            ))
          })()
        ) : hasPlayedToday && revealedWord ? (
          revealedWord.split('').map((letter, index) => (
            <div
              key={index}
              className={`letter-box ${
                revealedWon
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-red-500 text-white border-red-500'
              } uppercase`}
            >
              {letter.toUpperCase()}
            </div>
          ))
        ) : (
          examples.map((letter, index) => (
            <div
              key={index}
              className={`letter-box ${isLocked && !hasPlayedToday ? 'bg-gray-700' : ''}`}
            >
              {isLocked && !hasPlayedToday ? '?' : letter}
            </div>
          ))
        )}
      </div>

      {isLocked && !hasPlayedToday && daysCompleted > 0 && (
        <div className="progress-container">
          <div
            className="progress-bar"
            style={{ width: `${progressPercent}%` }}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
          />
          <span className="progress-text">
            {daysCompleted}/{totalDays} days completed · {progressPercent}%
          </span>
        </div>
      )}

      <div className="text-center mt-auto">
        <button
          onClick={isDisabled ? undefined : onPlay}
          disabled={isDisabled}
          className={`play-button ${
            hasPlayedToday ? 'bg-gray-500' : isLocked ? 'bg-gray-600' : ''
          } flex items-center justify-center mx-auto`}
          aria-label={ariaLabel}
        >
          {isLocked && !hasPlayedToday ? <FaLock aria-hidden="true" /> : null}
          {buttonText}
        </button>
      </div>
    </div>
  )
}
